import { useCallback, useMemo, useState, useEffect } from 'react';
import { format } from 'date-fns';
// TODO: migrate to @dnd-kit/react (new API with type/accept/group constraints)
import { arrayMove } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';
import { Workorder } from '@certhon/models';
import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { Loader2 } from 'lucide-react';
import {
  useDayWorkorders,
  useRoutes,
  useUpdateDayPlanningMutation,
  sortWorkorders,
  useAllDayWorkorders,
  useMultiTechRoutes,
  useCanEditDayPlanning,
  useGeocodedCoordinates,
  useWorkorderRouteErrors,
  useMultiTechRouteErrors,
  usePlangroups,
  useSelectedPlangroups,
  useRoutingKeyStatus,
  useOptimizeRouteMutation,
  useManualRouteUpdateMutation,
} from './dayplanning.hooks';
import { DayNavigation } from './components/DayNavigation';
import { DayPlanningMap } from './components/DayPlanningMap';
import { DayPlanningFilters } from './components/DayPlanningFilters';
import { DayPlanningRouteStatus } from './components/DayPlanningRouteStatus';
import { DayPlanningErrorAlerts } from './components/DayPlanningErrorAlerts';
import { DayPlanningWorkorderList } from './components/DayPlanningWorkorderList';
import { MapViewToggle } from '../components/MapViewToggle';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useUserMap } from '@/features/users/users.hooks';
import { useStackNavigate } from '@/app/navigation/stack-links';
import {
  TECHNICIAN_COLORS,
  useRouteResolution,
  useRouteTotals,
  countFailedRoutes,
  hasRateLimitError,
  useAutoOptimization,
  useMultiTechAutoOptimization,
  MapErrorBoundary,
  STORAGE_KEYS,
} from '../shared';

export const DayPlanningPanel = ({
  params,
  path,
  ...panelProps
}: RoutedPanelProps) => {
  const date = params.date || format(new Date(), 'yyyy-MM-dd');
  const canEdit = useCanEditDayPlanning();
  const userMap = useUserMap<'recnum'>('recnum');
  const navigate = useStackNavigate();

  // Get selected technician from URL params
  const selectedTechId = params.techId ? parseInt(params.techId, 10) : null;

  // Navigate to change selected technician
  const setSelectedTechId = useCallback(
    (techId: number | null) => {
      if (techId === null) {
        navigate({
          to: '/day-planning/$date',
          params: { date },
        });
      } else {
        navigate({
          to: '/day-planning/$date/$techId',
          params: { date, techId: techId.toString() },
        });
      }
    },
    [navigate, date],
  );

  // Fetch all technicians' workorders
  const { data: allWorkordersByTech, isLoading: isLoadingAll } =
    useAllDayWorkorders(date);

  // Plangroup filtering
  const { data: plangroups = [] } = usePlangroups();
  const [selectedPlangroups] = useSelectedPlangroups();

  // Filter workordersByTech by selected plangroups
  const filteredWorkordersByTech = useMemo(() => {
    if (!allWorkordersByTech) return undefined;
    if (selectedPlangroups.length === 0) return allWorkordersByTech; // Show all when none selected

    // Get user IDs from selected plangroups
    const allowedUserIds = new Set(
      plangroups
        .filter((pg) => selectedPlangroups.includes(pg.id))
        .flatMap((pg) => pg.user_ids),
    );

    // Filter the workordersByTech map
    return new Map(
      Array.from(allWorkordersByTech.entries()).filter(([userId]) =>
        allowedUserIds.has(userId),
      ),
    );
  }, [allWorkordersByTech, plangroups, selectedPlangroups]);

  // Determine if we're in multi-technician mode (all techs shown)
  const isMultiTechView = selectedTechId === null && canEdit;

  // Get workorders for the selected technician (or current user if no selection)
  const selectedUserId = selectedTechId ?? undefined;

  // Single technician view - fetch current user's workorders only if not in admin view
  const { data: currentUserWorkorders, isLoading: isLoadingCurrentUser } =
    useDayWorkorders(date);

  // Determine which workorders to use based on selection
  const workorders = useMemo(() => {
    if (isMultiTechView) {
      return []; // Not used in multi-tech view
    }

    // If a technician is selected and we have the data, use it
    if (selectedTechId && filteredWorkordersByTech) {
      return filteredWorkordersByTech.get(selectedTechId) ?? [];
    }

    // Otherwise use current user's workorders
    return currentUserWorkorders ?? [];
  }, [
    isMultiTechView,
    selectedTechId,
    filteredWorkordersByTech,
    currentUserWorkorders,
  ]);

  const serverSorted = useMemo(() => sortWorkorders(workorders), [workorders]);

  // Local state for optimistic reordering during drag
  const [localOrder, setLocalOrder] = useState<Workorder[]>([]);

  // Sync local state with server data when it changes
  useEffect(() => {
    setLocalOrder(serverSorted);
  }, [serverSorted]);

  const isLoadingWorkorders = isMultiTechView
    ? isLoadingAll
    : selectedTechId
      ? isLoadingAll
      : isLoadingCurrentUser;

  // Workorders to display (multi-tech or single tech)
  const displayWorkorders = useMemo(() => {
    if (isMultiTechView && filteredWorkordersByTech) {
      // Flatten all workorders from all technicians, sorted
      const allWos: Workorder[] = [];
      filteredWorkordersByTech.forEach((wos) => {
        allWos.push(...sortWorkorders(wos));
      });
      return allWos;
    }
    return localOrder.length > 0 ? localOrder : serverSorted;
  }, [isMultiTechView, filteredWorkordersByTech, localOrder, serverSorted]);

  // Per-technician index map for multi-tech view (recnum -> 1-based index within technician's list)
  const perTechIndexMap = useMemo(() => {
    const map = new Map<number, number>();
    if (isMultiTechView && filteredWorkordersByTech) {
      filteredWorkordersByTech.forEach((wos) => {
        const sorted = sortWorkorders(wos);
        sorted.forEach((wo, idx) => {
          map.set(wo.recnum, idx + 1); // 1-based index
        });
      });
    }
    return map;
  }, [isMultiTechView, filteredWorkordersByTech]);

  // Routes (single or multi-tech)
  const {
    data: singleTechRoutes,
    isLoading: isLoadingSingleRoutes,
    error: singleError,
  } = useRoutes(
    isMultiTechView ? [] : displayWorkorders,
    selectedTechId ?? undefined,
  );

  const {
    data: multiTechRoutes,
    isLoading: isLoadingMultiRoutes,
    error: multiError,
    progress: multiTechProgress,
  } = useMultiTechRoutes(filteredWorkordersByTech);

  const routes = isMultiTechView ? multiTechRoutes : singleTechRoutes;
  const isLoadingRoutes = isMultiTechView
    ? isLoadingMultiRoutes
    : isLoadingSingleRoutes;
  const error = isMultiTechView ? multiError : singleError;

  // Geocode workorder addresses as fallback for when routing fails
  const { data: geocodedCoords } = useGeocodedCoordinates(displayWorkorders);

  // Get route errors per workorder (different hooks for single vs multi tech)
  const singleTechRouteErrors = useWorkorderRouteErrors(
    isMultiTechView ? undefined : displayWorkorders,
    isMultiTechView ? undefined : routes,
  );
  const multiTechRouteErrors = useMultiTechRouteErrors(
    isMultiTechView ? filteredWorkordersByTech : undefined,
  );
  const routeErrors = isMultiTechView
    ? multiTechRouteErrors
    : singleTechRouteErrors;

  // Technician color mapping
  const technicianColors = useMemo(() => {
    if (!allWorkordersByTech) return new Map<number, string>();
    const colors = new Map<number, string>();
    const techIds = Array.from(allWorkordersByTech.keys()).sort();
    techIds.forEach((id, idx) => {
      colors.set(id, TECHNICIAN_COLORS[idx % TECHNICIAN_COLORS.length]);
    });
    return colors;
  }, [allWorkordersByTech]);

  // Get color for single-tech view
  const singleTechColor = useMemo(() => {
    if (isMultiTechView) return undefined;
    if (selectedTechId && technicianColors.has(selectedTechId)) {
      return technicianColors.get(selectedTechId);
    }
    // For current user, try to find their color
    if (displayWorkorders.length > 0 && displayWorkorders[0].assigned_user_id) {
      return technicianColors.get(displayWorkorders[0].assigned_user_id);
    }
    return undefined;
  }, [isMultiTechView, selectedTechId, technicianColors, displayWorkorders]);

  // Count failed routes
  const failedRoutesCount = countFailedRoutes(routes);
  const hasRateLimit = hasRateLimitError(routes);

  // Calculate total distance and duration
  const routeTotals = useRouteTotals(routes);

  useEffect(() => {
    if (error) {
      toast.error('Could not load routes', {
        description: error.message || String(error),
      });
    }
  }, [error]);

  const updateMutation = useUpdateDayPlanningMutation(date, selectedTechId);
  const [showMap, setShowMap] = useLocalStorage(STORAGE_KEYS.showMap, true);

  // Route ordering clarity hooks
  const routingStatus = useRoutingKeyStatus(
    isMultiTechView ? undefined : displayWorkorders,
  );

  // Determine the effective user ID for route operations
  const routeUserId =
    selectedUserId ?? displayWorkorders[0]?.assigned_user_id ?? undefined;

  const optimizeMutation = useOptimizeRouteMutation(date, routeUserId);
  const manualRouteMutation = useManualRouteUpdateMutation(date, routeUserId);

  // Use shared auto-optimization hook (single-tech view)
  const { handleMutation, handleOptimizeRoute, willAutoOptimize } =
    useAutoOptimization({
      routingStatus,
      optimizeMutation,
      routeUserId,
      workorderCount: displayWorkorders.length,
      isMultiTechView,
    });

  // Use multi-tech auto-optimization hook (all-tech view)
  const { progress: multiTechOptimizationProgress } =
    useMultiTechAutoOptimization({
      workordersByTech: filteredWorkordersByTech,
      date,
      isAdmin: canEdit,
      isActive: isMultiTechView,
    });

  // Handle arrive time change
  const handleArriveTimeChange = useCallback(
    (workorder: Workorder, time: string | null) => {
      handleMutation(() =>
        updateMutation.mutateAsync({
          recnum: workorder.recnum,
          fields: { planned_arrive_time: time },
        }),
      );
    },
    [updateMutation, handleMutation],
  );

  // Handle route option change
  const handleRouteOptionChange = useCallback(
    (workorder: Workorder, option: 'direct' | 'via_hq') => {
      handleMutation(() =>
        updateMutation.mutateAsync({
          recnum: workorder.recnum,
          fields: { route_option: option },
        }),
      );
    },
    [updateMutation, handleMutation],
  );

  // Handle drag end - uses atomic manual route update (admin only)
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      // Only admins can manually reorder (sets sort_order_source = 'manual')
      if (!canEdit) {
        toast.error('Permission denied', {
          description: 'Only administrators can manually reorder workorders.',
        });
        return;
      }

      const { active, over } = event;

      if (!over || active.id === over.id) return;

      const oldIndex = localOrder.findIndex((wo) => wo.recnum === active.id);
      const newIndex = localOrder.findIndex((wo) => wo.recnum === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      // Optimistically update local state immediately
      const newOrder = arrayMove(localOrder, oldIndex, newIndex);
      setLocalOrder(newOrder);

      // Build route_info for atomic update (all workorders at once)
      const routeInfo = newOrder.map((wo, idx) => ({
        recnum: wo.recnum,
        time: idx === oldIndex ? null : wo.planned_arrive_time, // Clear time on moved item
        order: idx,
        route_option: wo.route_option ?? 'direct',
      }));

      // Use atomic manual route update (sets sort_order_source = 'manual')
      handleMutation(() => manualRouteMutation.mutateAsync(routeInfo));
    },
    [localOrder, canEdit, manualRouteMutation, handleMutation],
  );

  // Route resolution (handles via_hq complexity)
  const { getRouteForWorkorder, leadingRoute, trailingRoute } =
    useRouteResolution(displayWorkorders, routes);

  return (
    <Panel {...panelProps} title="Day Planning">
      <div className="mb-4 space-y-2">
        <DayNavigation date={date} />
        <div className="flex items-center justify-end">
          <MapViewToggle showMap={showMap} onToggleMap={setShowMap} />
        </div>
      </div>

      {/* Plangroup filter and technician selector (for admins) */}
      {filteredWorkordersByTech && (
        <DayPlanningFilters
          workordersByTech={filteredWorkordersByTech}
          selectedTechId={selectedTechId}
          onSelectTech={setSelectedTechId}
          userMap={userMap}
          canEdit={canEdit}
        />
      )}

      {isLoadingWorkorders || isLoadingAll ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
        </div>
      ) : displayWorkorders.length === 0 ? (
        <div className="text-muted-foreground py-12 text-center">
          No workorders scheduled for this day
        </div>
      ) : (
        <div
          className={cn(
            'flex flex-col gap-4',
            showMap && '@lg:grid @lg:grid-cols-2',
          )}
        >
          {/* Map */}
          {showMap && (
            <div
              className={cn(
                'order-first @lg:order-last',
                '@lg:sticky @lg:top-0 @lg:self-start',
              )}
            >
              <MapErrorBoundary
                resetKey={`${date}-${selectedTechId}-${displayWorkorders.length}`}
                fallbackClassName="h-64 @lg:h-[calc(100vh-20rem)] @lg:min-h-128"
              >
                <DayPlanningMap
                  workorders={displayWorkorders}
                  routes={routes}
                  geocodedCoords={geocodedCoords}
                  className="h-64 @lg:h-[calc(100vh-20rem)] @lg:min-h-128"
                  multiTechnician={isMultiTechView}
                  technicianColors={technicianColors}
                  selectedTechnicianId={
                    isMultiTechView ? undefined : selectedTechId
                  }
                />
              </MapErrorBoundary>
            </div>
          )}

          {/* List content */}
          <div>
            {/* Error alerts: optimization, rate limit, failed routes */}
            <DayPlanningErrorAlerts
              hasOptimizationError={optimizeMutation.isError}
              optimizationError={optimizeMutation.error}
              isOptimizing={optimizeMutation.isPending}
              onRetryOptimize={handleOptimizeRoute}
              hasRateLimit={hasRateLimit}
              failedRoutesCount={failedRoutesCount}
              multiTechProgress={
                isMultiTechView ? multiTechOptimizationProgress : undefined
              }
            />

            {/* Route summary: status badge + totals */}
            {!isMultiTechView && displayWorkorders.length > 0 && (
              <DayPlanningRouteStatus
                routingStatus={routingStatus}
                routeTotals={routeTotals}
                isLoadingRoutes={isLoadingRoutes}
                isOptimizing={optimizeMutation.isPending}
                willAutoOptimize={willAutoOptimize}
                onOptimize={handleOptimizeRoute}
              />
            )}

            {/* Workorder list with drag-drop or read-only multi-tech view */}
            <DayPlanningWorkorderList
              workorders={displayWorkorders}
              isMultiTechView={isMultiTechView}
              isLoadingRoutes={isLoadingRoutes}
              technicianColors={technicianColors}
              singleTechColor={singleTechColor}
              perTechIndexMap={perTechIndexMap}
              userMap={userMap}
              routeErrors={routeErrors}
              onDragEnd={handleDragEnd}
              onArriveTimeChange={handleArriveTimeChange}
              onRouteOptionChange={handleRouteOptionChange}
              getRouteForWorkorder={getRouteForWorkorder}
              leadingRoute={leadingRoute}
              trailingRoute={trailingRoute}
              linkBase={path}
            />
          </div>
        </div>
      )}
    </Panel>
  );
};
